/**
 * Turns a row of cards into a swipeable slider with a next arrow and a progress thumb.
 *
 * The scrolling itself is native — the section's CSS turns its card row into a scroll-snap
 * track at the mobile breakpoint, so one set of markup serves desktop and mobile and touch
 * swiping, momentum and keyboard access all come from the browser. This element only adds
 * the two affordances the designs show on top of that:
 *
 *   - prev and next arrows that move exactly one card, wrapping around at both ends
 *   - a scrollbar-style thumb whose width is the visible fraction of the track and whose
 *     offset follows the scroll position
 *
 * Parts are found by data attribute rather than class, so the same component drives sections
 * with different class prefixes:
 *
 *   [data-slider-track] — the scrolling element; its children are the cards
 *   [data-slider-prev]  — optional previous button
 *   [data-slider-next]  — optional next button
 *   [data-slider-thumb] — optional progress thumb
 *
 * The arrows loop: next at the last card rewinds to the first, prev at the first jumps to
 * the last. That is a wrap-around, not a cloned infinite carousel — the cards hold
 * <product-form-component> elements whose ids derive from the section and product, so
 * duplicating them would collide those ids and break add to cart.
 *
 * Because navigation never dead-ends, neither arrow is ever disabled, so this element no
 * longer reflects `data-at-start` / `data-at-end`. It sets only `data-scrollable`, while
 * there is anywhere to scroll at all. Sections keep their end-state rules; with the
 * attributes absent they simply never match, leaving both arrows live at every position.
 *
 * Used by sections/ingredients-product-grid.liquid and sections/complete-your-routine.liquid.
 */

class CardRowSlider extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement | null} */
  #thumb = null;

  /** @type {HTMLElement | null} */
  #prev = null;

  /** @type {HTMLElement | null} */
  #next = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-slider-track]');
    if (!this.#track) return;

    this.#thumb = this.querySelector('[data-slider-thumb]');
    this.#prev = this.querySelector('[data-slider-prev]');
    this.#next = this.querySelector('[data-slider-next]');

    this.#track.addEventListener('scroll', this.#scheduleUpdate, { passive: true });
    this.#prev?.addEventListener('click', this.#handlePrev);
    this.#next?.addEventListener('click', this.#handleNext);

    // Card widths and the visible fraction both change on resize and orientation change.
    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#resizeObserver.observe(this.#track);

    this.#update();
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#scheduleUpdate);
    this.#prev?.removeEventListener('click', this.#handlePrev);
    this.#next?.removeEventListener('click', this.#handleNext);

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }

    this.#track = null;
    this.#thumb = null;
    this.#prev = null;
    this.#next = null;
  }

  #scheduleUpdate = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  /** The distance of one card plus the track's gap, so both arrows move by the same amount. */
  #step() {
    const track = this.#track;
    if (!track) return 0;

    const card = track.firstElementChild;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;

    return card instanceof HTMLElement ? card.offsetWidth + gap : track.clientWidth * 0.8;
  }

  /** The furthest the track can scroll. Sub-pixel layout makes this a fractional value. */
  #maxScroll() {
    const track = this.#track;
    return track ? Math.max(0, track.scrollWidth - track.clientWidth) : 0;
  }

  /**
   * Move one card in `direction` (-1 back, 1 forward), wrapping at both ends.
   *
   * The target is computed and clamped here rather than handed to `scrollBy`, which is what
   * keeps the track from coming to rest past its own content: an explicit `scrollTo` can
   * never overshoot, whereas a relative scroll near the end depends on the browser clamping
   * it for us. The 1px tolerances absorb the fractional scroll positions that snapping and
   * sub-pixel card widths leave behind — without them, an "at the end" test can sit half a
   * pixel short and refuse to wrap.
   */
  #move(direction) {
    const track = this.#track;
    if (!track) return;

    const max = this.#maxScroll();
    const current = track.scrollLeft;
    let target;

    if (direction > 0) {
      target = current >= max - 1 ? 0 : Math.min(current + this.#step(), max);
    } else {
      target = current <= 1 ? max : Math.max(current - this.#step(), 0);
    }

    track.scrollTo({ left: target, behavior: 'smooth' });
  }

  /** Go back by one card, wrapping to the end from the first card. */
  #handlePrev = () => {
    this.#move(-1);
  };

  /** Advance by one card, wrapping to the start from the last card. */
  #handleNext = () => {
    this.#move(1);
  };

  #update = () => {
    const track = this.#track;
    if (!track) return;

    const { scrollWidth, clientWidth, scrollLeft } = track;

    // On desktop the row is a grid with nothing to scroll, so the whole slider UI is inert
    // (and hidden by CSS).
    const overflow = scrollWidth - clientWidth;

    if (overflow <= 1) {
      this.removeAttribute('data-scrollable');
      return;
    }

    this.setAttribute('data-scrollable', '');

    if (this.#thumb) {
      const size = Math.min(clientWidth / scrollWidth, 1);
      const offset = scrollLeft / scrollWidth;

      this.#thumb.style.width = `${size * 100}%`;
      // A percentage inline-start margin resolves against the bar, which is what we want —
      // a percentage translate would resolve against the thumb's own width.
      this.#thumb.style.marginInlineStart = `${offset * 100}%`;
    }
  };
}

if (!customElements.get('card-row-slider')) {
  customElements.define('card-row-slider', CardRowSlider);
}
